import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Loader2, FolderOpen, UserCircle, Save } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChangePasswordForm from "@/components/ChangePasswordForm";
import CaseProgress from "@/components/CaseProgress";
import clientApi from "@/lib/clientApi";
import { useClientAuth } from "@/context/ClientAuthContext";
import { useLang } from "@/context/LanguageContext";
import { validatePersonName, PHONE_PATTERN } from "@/lib/validation";

export default function ClientPortalPage() {
  const { t } = useLang();
  const { user, checking, logout, updateUser } = useClientAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("cases");
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const loadCases = useCallback(async () => {
    setLoadingCases(true);
    try {
      const { data } = await clientApi.get("/client/cases");
      setCases(data.items || []);
    } catch {
      toast.error(t("portal.loadCasesError"));
    } finally {
      setLoadingCases(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (checking || !user) return;
    loadCases();
    // Lightweight polling (same interval as the admin inquiry/appointment alert hooks) so a
    // stage change or note edit from staff shows up here without the client refreshing.
    const interval = setInterval(loadCases, 20000);
    return () => clearInterval(interval);
  }, [checking, user, loadCases]);

  useEffect(() => {
    if (user) setProfileForm({ name: user.name || "", phone: user.phone || "" });
  }, [user]);

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--bg)]">
        <Loader2 size={32} className="text-[var(--gold)] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/portal/login" replace />;

  async function saveProfile(e) {
    e.preventDefault();
    const nameError = validatePersonName(profileForm.name);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    if (profileForm.phone.trim() && !PHONE_PATTERN.test(profileForm.phone.trim())) {
      toast.error("Please enter a valid phone number (digits only).");
      return;
    }
    setSavingProfile(true);
    try {
      await clientApi.put("/client/profile", profileForm);
      updateUser(profileForm);
      toast.success(t("portal.profileUpdated"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("portal.profileUpdateError"));
    } finally {
      setSavingProfile(false);
    }
  }

  return (
    <>
      <Header />
      <main className="bg-[var(--bg)] min-h-screen">
        <section className="pt-28 pb-12 bg-radial-gold border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="overline mb-2">{t("portal.title")}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-black text-white">
                {t("portal.welcome")}{user.name ? `, ${user.name}` : ""}
              </h1>
            </div>
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="btn-outline-gold text-sm py-2 px-4"
              data-testid="portal-logout"
            >
              <LogOut size={14} /> {t("portal.signOut")}
            </button>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-2 mb-8 border-b border-white/5">
              <button
                onClick={() => setTab("cases")}
                className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
                  tab === "cases" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
                }`}
                data-testid="portal-tab-cases"
              >
                <FolderOpen size={15} className="inline mr-1.5" /> {t("portal.myApplications")} ({cases.length})
              </button>
              <button
                onClick={() => setTab("profile")}
                className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
                  tab === "profile" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
                }`}
                data-testid="portal-tab-profile"
              >
                <UserCircle size={15} className="inline mr-1.5" /> {t("portal.profile")}
              </button>
            </div>

            {tab === "cases" && (
              <div className="space-y-6" data-testid="portal-cases-panel">
                {loadingCases && <div className="text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
                {!loadingCases && cases.length === 0 && (
                  <div className="card-dark p-12 text-center text-white/55" data-testid="portal-no-cases">
                    {t("portal.noApplications")}
                  </div>
                )}
                {cases.map((c) => (
                  <div key={c.id} className="card-dark p-6">
                    <CaseProgress caseItem={c} />
                  </div>
                ))}
              </div>
            )}

            {tab === "profile" && (
              <div className="space-y-6 max-w-xl" data-testid="portal-profile-panel">
                <div className="card-dark p-6">
                  <h2 className="font-display text-xl font-bold text-white mb-4">{t("portal.yourDetails")}</h2>
                  <form onSubmit={saveProfile} className="space-y-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.email")}</label>
                      <input value={user.email} disabled className="input-dark opacity-60 cursor-not-allowed" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("portal.fullName")}</label>
                      <input
                        value={profileForm.name}
                        onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))}
                        className="input-dark"
                        data-testid="portal-profile-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("portal.phone")}</label>
                      <input
                        type="tel"
                        inputMode="tel"
                        pattern="[0-9+()\-.\s]{6,30}"
                        maxLength={30}
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm((f) => ({ ...f, phone: e.target.value }))}
                        className="input-dark"
                        data-testid="portal-profile-phone"
                      />
                    </div>
                    <button type="submit" disabled={savingProfile} className="btn-gold text-sm py-2 px-5">
                      {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      {t("portal.save")}
                    </button>
                  </form>
                </div>

                <div className="card-dark p-6">
                  <h2 className="font-display text-xl font-bold text-white mb-4">{t("portal.changePassword")}</h2>
                  <ChangePasswordForm apiClient={clientApi} endpoint="/client/change-password" />
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
